import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const capabilities = [
  {
    videoSrc: "/videos/voice-control-demo.mp4",
    title: "Simple Generation",
    description:
      "AI that understands your commands and creates super powerful automations. Say goodbye to complex configurations and flowcharts.",
  },
  {
    videoSrc: "/videos/intelligent-automation-demo.mp4",
    title: "Flexible Editing",
    description:
      "Edit your automations with ease. Either instructing the AI or quick configuring key parameters from the UI.",
  },
  {
    videoSrc: "/videos/home-agent-demo.mp4",
    title: "Instant Control",
    description:
      "Fulfill your real-time needs by instructing an instant automation. Temporarily alter the behavior of your background automations with quick interactions.",
  },
  {
    videoSrc: "/videos/simple-generation-demo.mp4",
    title: "Hybrid Environment",
    description:
      "Host your automations on cloud for easy access or on your local devices for better privacy, or even a hybrid way for both strengths.",
  },
  {
    videoSrc: "/videos/universal-integration-demo.mp4",
    title: "Interactive Dashboard",
    description:
      "Highly interactive dashboard to manage your automations, devices, settings, and more.",
  },
  {
    videoSrc: "/videos/smart-insights-demo.mp4",
    title: "Smart Insights",
    description:
      "Explore the community shared and AI suggested automations. Deep understanding of your home patterns with intelligent analytics.",
  },
];

function CapabilitiesSection() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, lg: 4 },
        backgroundColor: theme.palette.background.glassLight,
        backdropFilter: "blur(10px)",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Chip
            label="User Experience"
            sx={{
              mb: 2,
              backgroundColor: theme.palette.chip.primary.background,
              color: theme.palette.chip.primary.color,
              border: `1px solid ${theme.palette.chip.primary.border}`,
              fontWeight: 600,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Everything You Need for{" "}
            <Box
              component="span"
              sx={{
                background: theme.palette.brand.gradientText,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smart Home Automation
            </Box>
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {capabilities.map((capability, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: theme.palette.background.card,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 3,
                  transition: `all ${theme.transitions.duration.standard}ms ease`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: theme.palette.background.cardHover,
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      mb: 2,
                      fontSize: "1.25rem",
                    }}
                  >
                    {capability.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.tertiary,
                      lineHeight: 1.6,
                      mb: 3,
                      flex: 1,
                    }}
                  >
                    {capability.description}
                  </Typography>

                  {/* Video */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 200,
                      borderRadius: 2,
                      overflow: "hidden",
                      position: "relative",
                      backgroundColor: theme.palette.background.glassLight,
                    }}
                  >
                    <video
                      width="100%"
                      height="100%"
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                      onError={(e) => {
                        // Fallback: hide video and show placeholder if video fails to load
                        e.target.style.display = "none";
                        const placeholder =
                          e.target.parentElement.querySelector(
                            ".video-placeholder",
                          );
                        if (placeholder) {
                          placeholder.style.display = "flex";
                        }
                      }}
                    >
                      <source src={capability.videoSrc} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    {/* Fallback placeholder */}
                    <Box
                      className="video-placeholder"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        background: theme.palette.brand.gradient,
                        color: theme.palette.text.white,
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                      }}
                    >
                      Demo Video
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default CapabilitiesSection;
